/**
 * useGoalState — React hook that reduces goal SSE events into client-side GoalState.
 *
 * Consumes goal_* events from the SSE stream and maintains a reactive GoalState
 * that drives the GoalProgressStrip and other goal UI.
 */
import { useCallback, useReducer } from "react";

import type {
  GoalBudget,
  GoalPhase,
  GoalPlanItem,
  GoalSpent,
  GoalSSEEvent,
  GoalState,
  PauseReason,
} from "../goalTypes";
import { isGoalEvent } from "../goalTypes";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: "goal_activated"; goalId: string; objective: string; toPhase: GoalPhase; budget?: GoalBudget }
  | { type: "phase_changed"; toPhase: GoalPhase; reason?: string; pauseReason?: PauseReason }
  | { type: "plan_updated"; planVersion: number; items?: GoalPlanItem[] }
  | { type: "budget_warning"; dimension: string }
  | { type: "budget_updated"; spent: Partial<GoalSpent> }
  | { type: "cancelled" }
  | { type: "complete" }
  | { type: "reset" };

const DEFAULT_BUDGET: GoalBudget = { max_cost_usd: 2.0, max_turns: 20, max_duration_s: 600 };
const DEFAULT_SPENT: GoalSpent = { cost_usd: 0, turns: 0, duration_s: 0 };

function reducer(state: GoalState | null, action: Action): GoalState | null {
  switch (action.type) {
    case "goal_activated":
      return {
        goalId: action.goalId,
        objective: action.objective,
        phase: action.toPhase ?? "planning",
        pauseReason: null,
        plan: [],
        planVersion: 1,
        budget: action.budget ?? DEFAULT_BUDGET,
        spent: DEFAULT_SPENT,
        strategyNote: null,
        budgetWarningDimension: null,
      };

    case "phase_changed":
      if (!state) return null;
      return {
        ...state,
        phase: action.toPhase,
        pauseReason: action.toPhase === "paused" ? (action.pauseReason ?? action.reason as PauseReason ?? null) : null,
      };

    case "plan_updated":
      if (!state) return null;
      return {
        ...state,
        planVersion: action.planVersion,
        plan: action.items ?? state.plan,
      };

    case "budget_warning":
      if (!state) return null;
      return { ...state, budgetWarningDimension: action.dimension };

    case "budget_updated":
      if (!state) return null;
      return {
        ...state,
        spent: { ...state.spent, ...action.spent },
      };

    case "cancelled":
    case "reset":
      return null;

    case "complete":
      if (!state) return null;
      return { ...state, phase: "complete" };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// SSE → Action mapper
// ---------------------------------------------------------------------------

function sseToAction(event: GoalSSEEvent): Action | null {
  switch (event.kind) {
    case "goal_activated":
      return {
        type: "goal_activated",
        goalId: event.goal_id,
        objective: event.objective,
        toPhase: event.to_phase,
      };

    case "goal_phase_changed":
    case "goal_paused":
    case "goal_resumed":
      return {
        type: "phase_changed",
        toPhase: event.to_phase,
        reason: event.reason,
        pauseReason: event.reason as PauseReason | undefined,
      };

    case "goal_plan_updated":
      return {
        type: "plan_updated",
        planVersion: event.plan_version,
      };

    case "goal_budget_warning":
      return { type: "budget_warning", dimension: event.dimension };

    case "goal_cancelled":
      return { type: "cancelled" };

    case "goal_complete":
      return { type: "complete" };

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseGoalStateReturn {
  /** Current goal state, or null if no active goal. */
  goal: GoalState | null;
  /** Feed an SSE frame to the reducer. No-op if not a goal event. */
  handleFrame: (frame: Record<string, unknown>) => void;
  /** Reset goal state (e.g., on session switch). */
  reset: () => void;
}

export function useGoalState(): UseGoalStateReturn {
  const [goal, dispatch] = useReducer(reducer, null);

  const handleFrame = useCallback(
    (frame: Record<string, unknown>) => {
      if (!isGoalEvent(frame as { kind?: string })) return;
      const action = sseToAction(frame as unknown as GoalSSEEvent);
      if (action) dispatch(action);
    },
    [],
  );

  const reset = useCallback(() => dispatch({ type: "reset" }), []);

  return { goal, handleFrame, reset };
}

// Export reducer + sseToAction for testing
export { reducer, sseToAction };
